/**
 * T-S2-A-3 · 录入辅助工具栏
 * 包含：应用模板下拉 / 复制上一个 / 保存草稿
 */
import { useState } from "react";
import { ChevronDown, Copy, FileText, Save } from "lucide-react";
import { FORM_TEMPLATES } from "./formTemplates";

interface Props {
  /** 模块名，用于查找 FORM_TEMPLATES */
  module: string;
  /** 应用模板回调 */
  onApplyTemplate: (data: Record<string, unknown>) => void;
  /** 复制上一个回调（不传则不显示按钮） */
  onCopyLast?: () => void;
  /** 是否有上一条数据 */
  hasLastEntry?: boolean;
  /** 草稿保存时间（格式化后的字符串，如 "14:22"） */
  draftSavedAt?: string | null;
  /** 手动保存草稿 */
  onSaveDraft?: () => void;
}

export function WizardToolbar({
  module,
  onApplyTemplate,
  onCopyLast,
  hasLastEntry,
  draftSavedAt,
  onSaveDraft,
}: Props) {
  const [open, setOpen] = useState(false);
  const templates = FORM_TEMPLATES[module] ?? [];

  if (templates.length === 0 && !hasLastEntry && !onSaveDraft) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      {/* 应用模板 */}
      {templates.length > 0 && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50"
          >
            <FileText size={11} />
            应用模板
            <ChevronDown size={11} className={open ? "rotate-180" : ""} />
          </button>
          {open && (
            <>
              {/* backdrop */}
              <div
                className="fixed inset-0 z-10"
                onClick={() => setOpen(false)}
              />
              <div className="absolute left-0 top-full z-20 mt-1 w-52 rounded-lg border border-slate-200 bg-white shadow-lg">
                {templates.map((t) => (
                  <button
                    key={t.name}
                    type="button"
                    onClick={() => {
                      onApplyTemplate(t.data);
                      setOpen(false);
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-slate-50 first:rounded-t-lg last:rounded-b-lg"
                  >
                    <div className="text-[12px] font-medium text-slate-800">
                      {t.name}
                    </div>
                    {t.description && (
                      <div className="mt-0.5 text-[11px] text-slate-400">
                        {t.description}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* 复制上一个 */}
      {hasLastEntry && onCopyLast && (
        <button
          type="button"
          onClick={onCopyLast}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50"
        >
          <Copy size={11} />
          复制上一个
        </button>
      )}

      {/* 保存草稿 */}
      {onSaveDraft && (
        <button
          type="button"
          onClick={onSaveDraft}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50"
        >
          <Save size={11} />
          保存草稿
        </button>
      )}

      {/* 草稿已保存提示 */}
      {draftSavedAt && (
        <span className="text-[11px] text-slate-400">
          草稿已存 {draftSavedAt}
        </span>
      )}
    </div>
  );
}
